import { Box, Card, CardContent, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import GroupsIcon from '@mui/icons-material/Groups';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PaidIcon from '@mui/icons-material/Paid';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { api } from '../../api/client';
import { StatWidget } from '../../components/StatWidget';
import { PageHeader } from '../../components/PageHeader';

interface Summary {
  totalClients: number; activeClients: number; pendingApprovals: number;
  executedTradesToday: number; totalVolumeUsd: string; dailyPnlUsd: string;
  exchangeStatus: Record<string, string>;
}

export default function AdminDashboard() {
  const { data } = useQuery({
    queryKey: ['admin-summary'],
    queryFn: async () => (await api.get('/admin/dashboard/summary')).data.data as Summary,
    refetchInterval: 10_000,
  });
  const { data: volume } = useQuery({
    queryKey: ['trade-volume'],
    queryFn: async () =>
      (await api.get('/admin/analytics/trade-volume?range=30d')).data.data as { date: string; value: number }[],
  });

  return (
    <Box>
      <PageHeader title="Admin Dashboard" subtitle="Platform overview & live trading activity" />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
        <StatWidget label="Total Clients" value={data?.totalClients ?? '—'} icon={<GroupsIcon />} color="#4f8cff" />
        <StatWidget label="Active Clients" value={data?.activeClients ?? '—'} icon={<HowToRegIcon />} color="#16c784" />
        <StatWidget label="Pending Approvals" value={data?.pendingApprovals ?? '—'} icon={<PendingActionsIcon />} color="#f5a623" />
        <StatWidget label="Trades Today" value={data?.executedTradesToday ?? '—'} icon={<SwapVertIcon />} color="#9b8cff" />
        <StatWidget label="Volume (USD)" value={data?.totalVolumeUsd ?? '—'} icon={<PaidIcon />} color="#4f8cff" />
        <StatWidget label="Daily PnL" value={data?.dailyPnlUsd ?? '—'} icon={<TrendingUpIcon />} color="#16c784" />
      </Box>

      <Card>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Trade Volume</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              Last 30 days
            </Typography>
          </Box>
          <Box sx={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={volume ?? []} margin={{ left: -16, right: 8, top: 8 }}>
                <defs>
                  <linearGradient id="volFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4f8cff" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#4f8cff" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: '#7c89a3', fontSize: 11 }} axisLine={false} tickLine={false} minTickGap={24} />
                <YAxis tick={{ fill: '#7c89a3', fontSize: 11 }} axisLine={false} tickLine={false} width={56} />
                <Tooltip
                  contentStyle={{ background: '#141d33', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10 }}
                  labelStyle={{ color: '#8b97b0' }}
                />
                <Area type="monotone" dataKey="value" stroke="#4f8cff" strokeWidth={2} fill="url(#volFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
